import React, { FC, Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { CheckIcon, MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { LOCATION_COPY, locationLabel, locationSubLabel } from "@1-now/booking-core";
import { useData } from "data/data-provider";
import { LocationListingModal } from "models/LocationModal";
import { branchHoursText } from "./availability";
import PickerLayer from "./PickerLayer";
import TripTile from "./TripTile";
import { useIsSheet } from "./useIsSheet";

// The delivery-radius maps bring react-leaflet; they are loaded when a branch with a radius is picked.
const LocationModal = lazy(() => import("../LocationModal"));
const LocationDropoffModal = lazy(() => import("../LocationDropoffModal"));

export interface LocationPickerBProps {
  kind: "pickup" | "dropoff";
  ourLocation: LocationListingModal[];
  /** Name of the field when the drop-off is entered separately; the pick-up is "Pick-up & drop-off" until then. */
  separateDropoff?: boolean;
  /** `chip`: the compact trigger of the scrolled header bar. */
  variant?: "tile" | "chip";
  /** Where the desktop popover sits (default: under the field). */
  desktopClassName?: string;
}

const priceTag = (item: any) => (item?.price === 0 ? "Free" : `$${item?.price}/trip`);
/** A branch at an airport says so, as the location lists of sixt.com tag theirs. */
const isAirport = (item: any) => /airport/i.test(`${item?.name ?? ""} ${item?.address ?? ""}`);

/**
 * 1NOW-11176: the Design B location picker for the car pages. A hairline field that opens a search box and a
 * list of the tenant's branches, after the location dropdowns of sixt.com and booking.com/cars: a thin pin,
 * the branch's name, its address and its hours for the trip's first day in muted text, an "Airport" tag where
 * it is one, its price at the right. On phones it is a bottom sheet with big touch rows. It is
 * LocationBranchInput / DropOffBranchLocationInput with another face: the same branches in the same order
 * (price, cheapest first), and the same selection (clear the typed address and the old place, then a branch
 * with a delivery radius opens its map, any other is chosen).
 */
const LocationPickerB: FC<LocationPickerBProps> = ({
  kind,
  ourLocation,
  separateDropoff = false,
  variant = "tile",
  desktopClassName = "absolute left-0 top-full mt-2 w-full min-w-[300px]",
}) => {
  const data = useData();
  const { ourCompany, startDate } = data;
  const pickup = kind === "pickup";
  const location = pickup ? data.pickupLocation : data.dropOffLocation;
  const setLocation = pickup ? data.setPickupLocation : data.setDropoffLocation;
  const searched: string = (pickup ? data.searchedPickupLoction : data.searchedDropoffLocation) ?? "";
  const setSearched = pickup ? data.setSearchedPickupLocation : data.setSearchedDropoffLocation;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const sheet = useIsSheet();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [radiusModal, setRadiusModal] = useState(false);
  const [radiusLocation, setRadiusLocation] = useState<any>(null);

  // The pick-up list offers every branch that is not switched off; the drop-off list only those that are
  // switched on, as the fields it replaces do.
  const options = useMemo(
    () =>
      ourLocation
        .filter((item) => (pickup ? item.active !== false : item.active === true))
        .sort((a, b) => a.price - b.price),
    [ourLocation, pickup],
  );
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((item) => `${item.name} ${item.address ?? ""}`.toLowerCase().includes(q));
  }, [options, query]);

  // A place cleared elsewhere (the page resets the trip) leaves no typed address behind.
  useEffect(() => {
    if (location === null) setSearched?.("");
  }, [location, setSearched]);

  // On desktop the search box takes the focus when the list opens. No scroll: iOS Safari scrolls the page
  // to the top when an input inside Design B's transformed wrappers is focused. Phones leave the keyboard
  // down until the renter taps the box.
  useEffect(() => {
    if (open && !sheet) searchRef.current?.focus({ preventScroll: true });
    if (!open) setQuery("");
  }, [open, sheet]);

  const choose = (item: any) => {
    setSearched?.("");
    setLocation?.(null);
    const hasRadius = pickup ? !!item.radius : item?.radius > 0;
    if (hasRadius) {
      setRadiusLocation(item);
      setRadiusModal(true);
    } else {
      setLocation?.(item);
    }
    setOpen(false);
  };

  const clear = () => {
    setSearched?.("");
    setLocation?.(null);
    setOpen(false);
  };

  const label = pickup
    ? separateDropoff
      ? LOCATION_COPY.pickUp.fieldLabel
      : LOCATION_COPY.same.fieldLabel
    : LOCATION_COPY.dropOff.fieldLabel;
  const prompt = pickup ? LOCATION_COPY.pickUp.prompt : LOCATION_COPY.dropOff.prompt;
  const hasValue = searched.length > 0 || !!location;
  const value = searched.length > 0 ? searched : location ? locationLabel(location, ourCompany?.company_name) : prompt;
  const sub = searched.length > 0 ? undefined : location ? locationSubLabel(location, ourCompany?.company_name) : undefined;

  return (
    <div ref={wrapperRef} className="relative" data-field={`location-${kind}`}>
      <TripTile
        layout={variant === "chip" ? "chip" : "field"}
        icon={<MapPinIcon className={variant === "chip" ? "h-4 w-4" : "h-5 w-5"} />}
        label={label}
        value={value}
        sub={sub || undefined}
        empty={!hasValue}
        active={open}
        trailing={location && !searched ? priceTag(location) : undefined}
        onClick={() => setOpen((was) => !was)}
      />

      <PickerLayer
        open={open}
        onClose={() => setOpen(false)}
        title={pickup ? "Pick-up location" : "Drop-off location"}
        wrapperRef={wrapperRef}
        desktopClassName={desktopClassName}
        name={`location-${kind}`}
      >
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-color/50" />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city or airport"
            aria-label="Search locations"
            className="h-11 w-full rounded-lg border border-text-color/20 bg-card-color pl-10 pr-3 text-[15px] text-text-color placeholder:text-text-color/50 focus:border-primary-brand focus:outline-none focus:ring-1 focus:ring-primary-brand"
          />
        </div>

        <ul className="m-0 mt-2 max-h-[min(52vh,360px)] list-none overflow-y-auto overscroll-contain p-0" data-testid={`location-list-${kind}`}>
          {shown.map((item) => {
            const selected = !searched && location?.id === item.id;
            const hours = branchHoursText(item, startDate, ourCompany);
            const address = locationSubLabel(item, ourCompany?.company_name);
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => choose(item)}
                  aria-pressed={selected}
                  className={`flex w-full items-center gap-3 rounded-lg px-2 py-3 text-left transition-colors hover:bg-text-color/5 ${
                    selected ? "bg-text-color/[0.04]" : ""
                  }`}
                >
                  <MapPinIcon className="h-5 w-5 shrink-0 text-text-color/60" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-[15px] font-medium text-text-color">
                        {locationLabel(item, ourCompany?.company_name)}
                      </span>
                      {isAirport(item) && (
                        <span className="shrink-0 rounded bg-text-color/[0.07] px-1.5 py-0.5 text-[11px] font-medium text-text-color/70">
                          Airport
                        </span>
                      )}
                    </span>
                    {(address || hours) && (
                      <span className="block truncate text-[13px] text-text-color/60">
                        {[address, hours].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-[13px] text-text-color/60">{priceTag(item)}</span>
                  {selected && <CheckIcon className="h-5 w-5 shrink-0 text-primary-brand" />}
                </button>
              </li>
            );
          })}
          {shown.length === 0 && (
            <li className="px-2 py-6 text-center text-sm text-text-color/60">No branch matches "{query.trim()}".</li>
          )}
        </ul>

        {options.length > 1 && hasValue && (
          <button type="button" onClick={clear} className="mt-2 px-2 text-[13px] font-medium text-text-color underline">
            Clear location
          </button>
        )}
      </PickerLayer>

      {radiusModal && (
        <Suspense fallback={null}>
          {pickup ? (
            <LocationModal
              isOpen={radiusModal}
              selectedLocation={radiusLocation}
              onClose={() => setRadiusModal(false)}
              ourCompany={ourCompany}
              setSearchedPickupLocation={setSearched}
              setPickupLocation={setLocation}
              ourLocation={ourLocation}
            />
          ) : (
            <LocationDropoffModal
              isOpen={radiusModal}
              selectedLocation={radiusLocation}
              onClose={() => setRadiusModal(false)}
              ourCompany={ourCompany}
              setSearchedDropoffLocation={setSearched}
              setDropoffLocation={setLocation}
              ourLocation={ourLocation}
            />
          )}
        </Suspense>
      )}
    </div>
  );
};

export default LocationPickerB;
